import React, { useState, useEffect } from "react";
import { NavLink, Link } from "react-router-dom";
import { Menu, X, ArrowUpRight } from "lucide-react";
import Logo from "../assets/quivix.png";

const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);
  const closeMobileMenu = () => setIsMenuOpen(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const navItems = [
    { label: "Overview", path: "/" },
    { label: "Apps", path: "/apps" },
    { label: "Invest", path: "/invest" },
    { label: "Careers", path: "/careers" },
    { label: "About", path: "/about" },
    { label: "Contact", path: "/contact" },
  ];

  return (
    <header className="fixed top-0 left-0 right-0 z-50 transition-all duration-300 px-4 sm:px-8 py-3">
      <div className={`max-w-6xl mx-auto rounded-full transition-all duration-500 px-6 py-3 flex items-center justify-between ${
        isScrolled 
          ? "bg-[#0a0a0c]/85 backdrop-blur-xl border border-white/10 shadow-[0_10px_30px_rgba(0,0,0,0.8)]" 
          : "bg-black/40 backdrop-blur-md border border-white/5"
      }`}>
        {/* Brand Logo */}
        <Link to="/" className="flex items-center gap-3 group" onClick={closeMobileMenu}>
          <div className="w-9 h-9 rounded-xl bg-neutral-900 border border-white/20 shadow-[0_0_20px_rgba(41,151,255,0.25)] p-1.5 flex items-center justify-center shrink-0 group-hover:border-blue-400 group-hover:scale-105 transition-all duration-300">
            <img src={Logo} alt="QuivixDigital Logo" className="w-full h-full object-contain" />
          </div>
          <div className="flex flex-col">
            <span className="text-white font-semibold text-base tracking-tight leading-none group-hover:text-blue-400 transition-colors">
              QuivixDigital
            </span>
            <span className="text-[10px] text-[#86868b] tracking-widest font-mono uppercase">
              Tech Studio
            </span>
          </div>
        </Link>

        {/* Desktop Navigation Links */}
        <nav className="hidden md:flex items-center gap-1 bg-white/[0.03] border border-white/[0.06] rounded-full px-4 py-1.5 backdrop-blur-sm">
          {navItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              className={({ isActive }) =>
                `text-xs font-medium px-4 py-1.5 rounded-full transition-all duration-200 ${
                  isActive
                    ? "text-sky-300 bg-sky-500/20 border border-sky-400/40 shadow-[0_0_15px_rgba(56,189,248,0.25)] font-bold"
                    : "text-[#86868b] hover:text-sky-300 hover:bg-sky-500/10 active:bg-sky-500/30 active:text-sky-200"
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        {/* Actions & CTA */}
        <div className="hidden md:flex items-center gap-3">
          <Link
            to="/invest"
            className="flex items-center gap-1.5 text-xs font-medium px-4 py-2 rounded-full bg-white text-black hover:bg-neutral-200 transition-all duration-300 hover:shadow-[0_0_20px_rgba(255,255,255,0.3)] active:scale-95"
          >
            <span>Investor Deck</span>
            <ArrowUpRight className="w-3.5 h-3.5" />
          </Link>
        </div>

        {/* Mobile Toggle Button */}
        <button
          onClick={toggleMenu}
          className="md:hidden text-white/80 hover:text-white p-2 rounded-full hover:bg-white/10 transition-colors"
          aria-label="Toggle Navigation"
        >
          {isMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      {/* Mobile Drawer Menu */}
      {isMenuOpen && (
        <div className="md:hidden fixed inset-x-4 top-20 bg-[#0c0c0e]/95 backdrop-blur-2xl border border-white/10 rounded-2xl p-6 shadow-2xl z-50 flex flex-col gap-4 animate-in fade-in slide-in-from-top-4 duration-300">
          <div className="flex flex-col gap-2">
            {navItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                onClick={closeMobileMenu}
                className={({ isActive }) =>
                  `text-sm font-medium px-4 py-3 rounded-xl transition-all ${
                    isActive
                      ? "text-sky-300 bg-sky-500/20 border border-sky-400/30 font-bold shadow-md shadow-sky-500/10"
                      : "text-[#86868b] hover:text-sky-300 hover:bg-sky-500/10 active:bg-sky-500/30 active:text-sky-200"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </div>

          <div className="pt-4 border-t border-white/10 flex flex-col gap-3">
            <Link
              to="/invest"
              onClick={closeMobileMenu}
              className="w-full flex items-center justify-center gap-2 text-xs font-semibold py-3 rounded-xl bg-white text-black hover:bg-neutral-200 transition-all"
            >
              <span>Investor Portal</span>
              <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
